import { useState, useEffect } from 'react'
import PoLineLink from '../common/PoLineLink'
import PoHeaderLink from '../common/PoHeaderLink'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { useMenuSettings } from '../../contexts/MenuSettingsContext'
import { useView } from '../../contexts/ViewContext'
import { purchasingAPI, type POLineSchedule, type POLineExpeditingSummary, type ExpeditingBucket } from '../../lib/api'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts'
import { TabProps } from './types'
import { useFormatters } from '../../contexts/PreferencesContext'
import { ChartWrapper } from './ChartWrapper'
import { Clock, AlertTriangle, CheckCircle, TrendingUp, Package, Loader2 } from 'lucide-react'

// Status colors for expediting
const STATUS_COLORS = {
  onTime: '#10b981',
  atRisk: '#f59e0b',
  late: '#ef4444',
  expedited: '#3b82f6',
}

// W3-T20 FIX-10: 'expedited' is gone as a BUCKET. The backend's Case was
// ordered, so a critical line that was overdue counted as 'late' and one due in
// three days as 'at_risk' — only a critical line comfortably in the future ever
// reached 'expedited', which made the bucket mean "flagged lines that are not
// urgent". Urgency and flag are two axes; the flag now rides alongside.
const BUCKET_LABELS: Record<ExpeditingBucket, string> = {
  on_time: 'On Time',
  at_risk: 'At Risk',
  late: 'Late',
}

const BUCKET_COLORS: Record<ExpeditingBucket, string> = {
  on_time: STATUS_COLORS.onTime,
  at_risk: STATUS_COLORS.atRisk,
  late: STATUS_COLORS.late,
}

/** Open PO-line statuses — closed and cancelled lines are not expediting work. */
const OPEN_LINE_STATUSES = 'pending,partially_received'

interface ExpediteStatus {
  status: string
  count: number
  value: number
  color: string
  [key: string]: string | number  // Index signature for Recharts compatibility
}

interface ExpediteBySupplier {
  supplier: string
  onTime: number
  atRisk: number
  late: number
  flagged: number
}

function todayISO(): string {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function daysLate(scheduleDate: string): number {
  const d = new Date(scheduleDate)
  const now = new Date()
  return Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24))
}

function daysLateClass(days: number): string {
  if (days > 14) return 'text-red-600 font-semibold'
  if (days > 7) return 'text-orange-600 font-medium'
  return ''
}

/**
 * Shape the server summary into the chart series the tab renders.
 *
 * Exported for tests: the bucket order and labels drive the pie, the legend
 * and the per-supplier stacked bars.
 */
export function summaryToSeries(summary: POLineExpeditingSummary): {
  status: ExpediteStatus[]
  suppliers: ExpediteBySupplier[]
} {
  const order: ExpeditingBucket[] = ['on_time', 'at_risk', 'late']
  const status = order.map((key) => ({
    status: BUCKET_LABELS[key],
    count: summary.buckets[key]?.count ?? 0,
    value: parseFloat(summary.buckets[key]?.value ?? '0') || 0,
    color: BUCKET_COLORS[key],
  }))
  const suppliers = summary.by_supplier.map((row) => ({
    supplier: row.supplier.length > 12 ? row.supplier.substring(0, 12) + '...' : row.supplier,
    onTime: row.on_time,
    atRisk: row.at_risk,
    late: row.late,
    flagged: row.flagged,
  }))
  return { status, suppliers }
}

export function ExpeditingTab({ data: _data, currency: _currency, filters }: TabProps) {
  const { formatCurrency, formatNumber } = useFormatters()
  const { isFeatureEnabled } = useMenuSettings()
  const { getViewQueryParams } = useView()
  const scheduleCollabEnabled = isFeatureEnabled('schedule_collaboration')
  const [expediteData, setExpediteData] = useState<ExpediteStatus[]>([])
  // FIX-10: the flag axis, carried beside the proximity buckets.
  const [flaggedCount, setFlaggedCount] = useState(0)
  const [supplierData, setSupplierData] = useState<ExpediteBySupplier[]>([])
  const [trendData, setTrendData] = useState<Array<{ month: string; expedited: number; late: number; atRisk: number }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pastDueSchedules, setPastDueSchedules] = useState<POLineSchedule[]>([])
  const [schedulesLoading, setSchedulesLoading] = useState(false)

  const filterSupplier = filters?.supplier || ''
  const filterSupplierGroup = filters?.supplierGroup || ''

  useEffect(() => {
    if (!scheduleCollabEnabled) return
    let cancelled = false
    const fetchPastDue = async () => {
      try {
        setSchedulesLoading(true)
        const res = await purchasingAPI.getSchedules({
          schedule_date__lt: todayISO(),
          status__in: 'scheduled,partially_shipped',
          page_size: 50,
          all_suppliers: true,
        })
        if (cancelled) return
        setPastDueSchedules(res.data.results || [])
      } catch (err) {
        console.error('Error fetching past-due schedules:', err)
        if (!cancelled) setPastDueSchedules([])
      } finally {
        if (!cancelled) setSchedulesLoading(false)
      }
    }
    fetchPastDue()
    return () => {
      cancelled = true
    }
  }, [scheduleCollabEnabled])

  useEffect(() => {
    let cancelled = false
    const fetchExpediteData = async () => {
      try {
        setLoading(true)
        setError(null)
        // One server-side GROUP BY over the open lines in the current view.
        // The tab used to page the whole line list into the browser (50+
        // requests on a PO-line-grain tenant) and showed zeros while it ran.
        const params: Record<string, string | number | boolean> = {
          status__in: OPEN_LINE_STATUSES,
          ...getViewQueryParams(),
        }
        if (filterSupplier) params['supplier'] = filterSupplier
        if (filterSupplierGroup) params['po__supplier__group'] = filterSupplierGroup
        const res = await purchasingAPI.getExpeditingSummary(params)
        if (cancelled) return
        const { status, suppliers } = summaryToSeries(res.data)
        setExpediteData(status)
        setFlaggedCount(res.data.flagged?.count ?? 0)
        setSupplierData(suppliers)
        // No monthly trend data available yet
        setTrendData([])
      } catch (err: unknown) {
        console.error('Error fetching expedite data:', err)
        if (cancelled) return
        setExpediteData([])
        setFlaggedCount(0)
        setSupplierData([])
        setTrendData([])
        setError('Expediting data could not be loaded. Please refresh to try again.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchExpediteData()
    return () => {
      cancelled = true
    }
  }, [getViewQueryParams, filterSupplier, filterSupplierGroup])

  const displayStatus = expediteData
  const displaySupplier = supplierData
  const displayTrend = trendData

  // Calculate totals
  const totalLines = displayStatus.reduce((sum, s) => sum + s.count, 0)
  const totalLate = displayStatus.find(s => s.status === 'Late')?.count || 0
  const totalAtRisk = displayStatus.find(s => s.status === 'At Risk')?.count || 0
  // FIX-10: the flag is its own axis on the summary, not a bucket in this
  // series — a flagged line is already counted in its proximity bucket.
  const totalFlagged = flaggedCount
  const lateValue = displayStatus.find(s => s.status === 'Late')?.value || 0
  // While the summary is in flight the tiles show a spinner, never a zero
  // that could be mistaken for the answer.
  const tile = (value: string | number) => (
    loading ? <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" /> : value
  )

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-card rounded-lg shadow-sm p-4">
        <h2 className="text-lg font-semibold">Expediting Dashboard</h2>
        <p className="text-sm text-muted-foreground">
          Track and manage delivery expediting across purchase order lines
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-sm text-red-800">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Lines</p>
                <p className="text-2xl font-bold">{tile(formatNumber(totalLines))}</p>
              </div>
              <Package className="h-8 w-8 text-gray-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">On Time</p>
                <p className="text-2xl font-bold text-green-600">
                  {tile(`${totalLines > 0 ? ((totalLines - totalLate - totalAtRisk) / totalLines * 100).toFixed(1) : '0.0'}%`)}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">At Risk</p>
                <p className="text-2xl font-bold text-amber-600">{tile(formatNumber(totalAtRisk))}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-red-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Late</p>
                <p className="text-2xl font-bold text-red-600">{tile(formatNumber(totalLate))}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                {/* FIX-10: this tile used to read a BUCKET that a critical
                    line could only reach if it was NOT urgent. It now reads the
                    flag itself, so a flagged line counts here whether it is
                    late, at risk or on time — the count a buyer acts on. */}
                <p className="text-sm text-muted-foreground">Flagged priority</p>
                <p className="text-2xl font-bold text-blue-600">{tile(formatNumber(totalFlagged))}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Status Distribution Pie */}
        <ChartWrapper
          title="Delivery Status Distribution"
          data={displayStatus}
          exportFilename="expedite-status-distribution"
        >
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={displayStatus.filter(d => d.count > 0)}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                dataKey="count"
                nameKey="status"
                label={false}
              >
                {displayStatus.filter(d => d.count > 0).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value: number, name: string) => [value, name]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2">
            {displayStatus.filter(d => d.count > 0).map((entry) => (
              <div key={entry.status} className="flex items-center gap-1.5 text-sm">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                <span>{entry.status}: {entry.count}</span>
              </div>
            ))}
          </div>
        </ChartWrapper>

        {/* Expediting Trend */}
        <ChartWrapper
          title="Expediting Trend"
          data={displayTrend}
          exportFilename="expedite-trend"
          legend={[
            { color: STATUS_COLORS.expedited, label: 'Expedited' },
            { color: STATUS_COLORS.late, label: 'Late' },
            { color: STATUS_COLORS.atRisk, label: 'At Risk' },
          ]}
        >
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={displayTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="expedited" stroke={STATUS_COLORS.expedited} strokeWidth={2} name="Expedited" />
              <Line type="monotone" dataKey="late" stroke={STATUS_COLORS.late} strokeWidth={2} name="Late" />
              <Line type="monotone" dataKey="atRisk" stroke={STATUS_COLORS.atRisk} strokeWidth={2} name="At Risk" />
            </LineChart>
          </ResponsiveContainer>
        </ChartWrapper>
      </div>

      {/* Supplier Expediting Status */}
      <ChartWrapper
        title="Expediting Status by Supplier"
        data={displaySupplier}
        exportFilename="expedite-by-supplier"
        legend={[
          { color: STATUS_COLORS.onTime, label: 'On Time' },
          { color: STATUS_COLORS.atRisk, label: 'At Risk' },
          { color: STATUS_COLORS.late, label: 'Late' },
          { color: STATUS_COLORS.expedited, label: 'Expedited' },
        ]}
      >
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={displaySupplier} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="supplier" tick={{ fontSize: 11 }} width={100} />
            <Tooltip />
            <Bar dataKey="onTime" stackId="a" fill={STATUS_COLORS.onTime} name="On Time" />
            <Bar dataKey="atRisk" stackId="a" fill={STATUS_COLORS.atRisk} name="At Risk" />
            <Bar dataKey="late" stackId="a" fill={STATUS_COLORS.late} name="Late" />
            <Bar dataKey="expedited" stackId="a" fill={STATUS_COLORS.expedited} name="Expedited" />
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>

      {/* Value at Risk */}
      <Card className="shadow-sm bg-gradient-to-r from-red-50 to-amber-50 border-red-200">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground font-medium">Value at Risk (Late Deliveries)</p>
              <p className="text-3xl font-bold text-red-700">{tile(formatCurrency(lateValue))}</p>
            </div>
            <AlertTriangle className="h-12 w-12 text-red-400" />
          </div>
        </CardContent>
      </Card>

      {/* Past-Due Schedule Lines (DE feature flag) */}
      {scheduleCollabEnabled && (
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Past-Due Schedule Lines</CardTitle>
          </CardHeader>
          <CardContent>
            {schedulesLoading ? (
              <p className="text-sm text-muted-foreground">Loading past-due schedules...</p>
            ) : pastDueSchedules.length === 0 ? (
              <p className="text-sm text-muted-foreground">No past-due schedules</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>PO #</TableHead>
                      <TableHead>Line #</TableHead>
                      <TableHead>Sched Line #</TableHead>
                      <TableHead>Part</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Schedule Date</TableHead>
                      <TableHead>Days Late</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead>Ack Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pastDueSchedules.map((s) => {
                      const days = daysLate(s.schedule_date)
                      return (
                        <TableRow key={s.id}>
                          {/* CR-1154: PO number links to the PO header; line to the line item. */}
                          <TableCell className="font-medium">
                            <PoHeaderLink poNumber={s.po_number}>{s.po_number}</PoHeaderLink>
                          </TableCell>
                          <TableCell>
                            <PoLineLink poLineId={s.po_line_id}>{s.line_number}</PoLineLink>
                          </TableCell>
                          <TableCell>{s.schedule_line_number}</TableCell>
                          <TableCell>{s.part_number}</TableCell>
                          <TableCell>{s.supplier_name}</TableCell>
                          <TableCell>{s.schedule_date}</TableCell>
                          <TableCell className={daysLateClass(days)}>{days}</TableCell>
                          <TableCell className="text-right">{s.schedule_qty}</TableCell>
                          <TableCell className="text-right">{s.balance_qty}</TableCell>
                          <TableCell>
                            {s.acknowledge_status ? (
                              <Badge variant="outline">{s.acknowledge_status}</Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Open PO lines only. Expediting status is calculated from the required delivery date:
          lines past due are "Late", lines due within 7 days are "At Risk", and lines flagged critical are "Expedited".
          Value at risk is in the reporting currency.
          {loading && ' Loading latest data...'}
        </p>
      </div>
    </div>
  )
}
